import type { AppProps } from 'next/app';
import { Global, ThemeProvider } from '@emotion/react';
import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { globalStyle, theme } from '@pairy/styles';
import { ToastContainer } from '@pairy/pds-react';
import { appWithTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride';
import { useRouter } from 'next/router';

import Scripts from '@/components/Scripts';
import usePageView from '@/hooks/usePageView';
import Head from '@/components/Head';
import FileUploadingElements from '@/components/Layouts/FileUploadingElements';
import { TourProvider, useTour } from '@/context/TourContext';
import useChangeTourStatus from '@/hooks/mutations/user/useChangeTourStatus';

const Joyride = dynamic(() => import('react-joyride'), { ssr: false });

dayjs.extend(utc);

function TourRunner() {
  const { run, steps, stepIndex, setStepIndex, setRun, setCompleted } =
    useTour();
  const router = useRouter();

  const { mutate: updateTourStatus } = useChangeTourStatus();

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { action, index, status, type } = data;

    // STEP 완료 후 이동 (next or prev)
    if (type === EVENTS.STEP_AFTER) {
      const step = steps[index];

      if (step?.autoClick && step.target) {
        const el = document.querySelector<HTMLElement>(step.target);
        if (el) {
          el.dispatchEvent(
            new MouseEvent('click', { bubbles: true, cancelable: true }),
          );

          // 다음 step target 등장 감시 후 stepIndex 증가
          const nextStep = steps[index + 1];
          if (nextStep?.target) {
            waitForElement(nextStep.target, () => {
              setStepIndex(index + 1);
            });
          }
          return; // autoClick에서는 여기서 멈춤
        }
      }

      // stepIndex 업데이트 (prev, next 대응)
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    // 투어 종료 시 처리
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setCompleted(true);

      // API 호출
      updateTourStatus(
        { tourStatus: status === STATUS.FINISHED ? 'COMPLETED' : 'SKIPPED' },
        {
          onSuccess: () => {
            if (status === STATUS.FINISHED) {
              router.push('/user/pdf');
            }
          },
        },
      );
    }
  };

  function waitForElement(
    selector: string,
    onFound: (el: HTMLElement) => void,
  ) {
    const el = document.querySelector<HTMLElement>(selector);
    if (el) {
      onFound(el);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element) {
        observer.disconnect();
        onFound(element);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  return (
    <Joyride
      steps={steps.filter((s) => !s.target.startsWith('NEXT_PAGE'))} // NEXT_PAGE는 표시 X
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      hideCloseButton
      showSkipButton
      disableCloseOnEsc
      disableOverlayClose
      hideBackButton
      scrollOffset={500}
      callback={handleJoyrideCallback}
      locale={{
        back: '이전',
        close: '닫기',
        last: '완료',
        next: '다음',
        nextLabelWithProgress: '다음 ({step}/{steps})',
        open: '다이얼로그 열기',
        skip: '끝내기',
      }}
      styles={{
        options: {
          arrowColor: theme.colors.white,
          backgroundColor: theme.colors.white,
          primaryColor: theme.colors.viridian_400,
          textColor: theme.colors.black,
          zIndex: 10000,
        },
      }}
    />
  );
}

function MyApp({ Component, pageProps }: AppProps) {
  const [queryClient] = useState(() => new QueryClient());
  usePageView();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <TourProvider>
          <Head />
          <Scripts />
          <ToastContainer />
          <TourRunner />
          <Component {...pageProps} />
          <FileUploadingElements />
          <Global styles={globalStyle} />
          <ReactQueryDevtools initialIsOpen={false} />
        </TourProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default appWithTranslation(MyApp);
